// components/Community/addCommunityToNode.tsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Autocomplete,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
} from "@mui/material";
import { createRoot } from "react-dom/client";
import theme from "theme";

export function addCommunityToNode(params: {
  nodeId: number;
  fieldName: string; // e.g. "people"
  communityAPI: { getAll: () => Promise<any[]> };
  getOutputNodes: (communityId: number) => Promise<any[]>;
  nodeAPI: {
    updateField: (id: number, field: string, value: any) => Promise<any>;
  };
  currentPeople: any[];
  onAdded?: () => void;
}) {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  const CleanupWrapper = () => {
    const [open, setOpen] = useState(true);
    const [communities, setCommunities] = useState<any[]>([]);
    const [selectedCommunity, setSelectedCommunity] = useState<any | null>(null);

    const [outputNodes, setOutputNodes] = useState<any[]>([]);
    const [selectedOutputNode, setSelectedOutputNode] = useState<any | null>(null);

    const [loadingOutputs, setLoadingOutputs] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleClose = () => {
      setOpen(false);
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, 200);
    };

    useEffect(() => {
      params.communityAPI.getAll().then(setCommunities);
    }, []);

    /** Existing people on current node */
    const existingIds = params.currentPeople
      .map((p: any) => (typeof p === "object" ? p.id : p))
      .filter((id): id is number => typeof id === "number");

    /** Load output nodes when community changes */
    useEffect(() => {
      if (!selectedCommunity) return;

      setLoadingOutputs(true);
      setSelectedOutputNode(null);
      setOutputNodes([]);

      params
        .getOutputNodes(selectedCommunity.id)
        .then((res) => {
          // Flatten: response[].nodes[]
          const nodes = res.flatMap((group: any) => group.nodes || []);
          setOutputNodes(nodes);
        })
        .finally(() => setLoadingOutputs(false));
    }, [selectedCommunity]);

    const handleAdd = async () => {
      if (!selectedOutputNode) return;

      console.log("selectedOutputNode:", selectedOutputNode);
      console.log("selectedOutputNode.people:", selectedOutputNode?.people);
      
      setSaving(true);

      /**
       * API already gives us people IDs directly
       */
      const peopleFromNode: number[] =
        selectedOutputNode.people?.filter(
          (id: any) => typeof id === "number"
        ) ?? [];

      const mergedIds = Array.from(
        new Set([...existingIds, ...peopleFromNode])
      );

      await params.nodeAPI.updateField(
        params.nodeId,
        params.fieldName,
        mergedIds
      );

      setSaving(false);
      handleClose();
      params.onAdded?.();
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: theme.custom.base.background.default.dark,
              color: theme.custom.base.text.primary.dark,
            },
          },
        }}
      >
        <DialogTitle>Add Community</DialogTitle>

        <DialogContent>
          {/* Community selector */}
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={communities}
              value={selectedCommunity}
              onChange={(_, value) => setSelectedCommunity(value)}
              getOptionLabel={(c) => c.name}
              renderInput={(params) => (
                <TextField {...params} label="Select Community" />
              )}
            />
          </Box>

          {/* Output node selector */}
          {selectedCommunity && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Select Output Node
              </Typography>

              {loadingOutputs ? (
                <CircularProgress size={20} />
              ) : (
                <List dense>
                  {outputNodes.map((node: any) => (
                    <ListItemButton
                      key={node.id}
                      selected={selectedOutputNode?.id === node.id}
                      onClick={() => setSelectedOutputNode(node)}
                    >
                      <ListItemText
                        primary={node.name}
                        secondary={`People: ${node.people_count ?? 0}`}
                      />
                    </ListItemButton>
                  ))}

                  {outputNodes.length === 0 && (
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      No output nodes found
                    </Typography>
                  )}
                </List>
              )}
            </Box>
          )}

          {/* Actions */}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button onClick={handleClose}>Cancel</Button>

            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={!selectedOutputNode || saving}
            >
              Add People
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  root.render(<CleanupWrapper />);
}
